import { useRef, useState } from 'react';
import { Loader2, Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/services/api';
import { Modal } from '@/components/ui/Modal';

/**
 * OnboardingModal — drop a resume PDF/DOCX; we extract everything via
 * /api/onboard/parse-resume, show the result as an editable form, and
 * /api/onboard/finalize commits the applicant + profile + master resume.
 *
 * "Foolproof" contract (Peter, 2026-06-09): the user touches nothing manually.
 * Every field is pre-filled by the resume parser. Edits are only for
 * corrections.
 */

interface ParsedProfile {
  personal: Record<string, string>;
  work_history: Array<Record<string, unknown>>;
  education_history: Array<Record<string, unknown>>;
  preferences: Record<string, unknown>;
  eeo: Record<string, string>;
  _review_flags?: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (applicantId: string) => void;
}

type Stage = 'drop' | 'extracting' | 'review' | 'saving';

const PERSONAL_FIELDS = [
  ['first_name', 'First name'],
  ['last_name', 'Last name'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['linkedin', 'LinkedIn URL'],
  ['street_address', 'Street address'],
  ['city', 'City'],
  ['state', 'State'],
  ['zip_code', 'ZIP'],
  ['university', 'University'],
  ['degree', 'Degree'],
  ['gpa', 'GPA'],
  ['grad_year', 'Graduation year'],
  ['salary', 'Salary expectation ($)'],
  ['years_exp', 'Years of experience'],
  ['citizenship', 'Citizenship (country)'],
] as const;

export function OnboardingModal({ open, onClose, onCreated }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>('drop');
  const [fileName, setFileName] = useState<string>('');
  const [profile, setProfile] = useState<ParsedProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applicantId, setApplicantId] = useState<string>('');
  // Server keeps the uploaded file in a temp dir keyed by session_id;
  // /finalize moves it to the per-applicant resumes/ dir.
  const [sessionId, setSessionId] = useState<string>('');

  function reset() {
    setStage('drop');
    setFileName('');
    setProfile(null);
    setError(null);
    setApplicantId('');
    setSessionId('');
  }

  async function handleFile(file: File) {
    setFileName(file.name);
    setError(null);
    setStage('extracting');
    try {
      const form = new FormData();
      form.append('file', file);
      const r = (await api.postForm('/onboard/parse-resume', form)) as {
        ok: true;
        profile: ParsedProfile;
        session_id: string;
      };
      setProfile(r.profile);
      setSessionId(r.session_id);
      const first = (r.profile.personal?.first_name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const last = (r.profile.personal?.last_name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
      setApplicantId(first && last ? `${first}${last[0] ?? ''}` : 'user');
      setStage('review');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setStage('drop');
    }
  }

  function onDrop(ev: React.DragEvent<HTMLDivElement>) {
    ev.preventDefault();
    const f = ev.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  }

  function setPersonalField(key: string, val: string) {
    setProfile((p) =>
      p ? { ...p, personal: { ...p.personal, [key]: val } } : p,
    );
  }

  async function confirmAndStart() {
    if (!profile || !applicantId) return;
    setStage('saving');
    try {
      const r = (await api.post('/onboard/finalize', {
        applicant_id: applicantId,
        profile,
        session_id: sessionId || undefined,
      })) as { ok: true; applicant_id: string };
      toast.success(`Created applicant ${r.applicant_id}`);
      onCreated(r.applicant_id);
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setStage('review');
    }
  }

  function closeAndReset() {
    reset();
    onClose();
  }

  const reviewFlags = profile?._review_flags ?? [];

  return (
    <Modal
      open={open}
      onClose={closeAndReset}
      title="Add user — drop a resume"
      size="lg"
      footer={
        stage === 'review' || stage === 'saving' ? (
          <>
            <button onClick={closeAndReset} className="btn-secondary" disabled={stage === 'saving'}>
              Cancel
            </button>
            <button
              onClick={confirmAndStart}
              disabled={stage === 'saving' || !applicantId}
              className="btn-primary"
            >
              {stage === 'saving' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Confirm &amp; start
                </>
              )}
            </button>
          </>
        ) : (
          <button onClick={closeAndReset} className="btn-secondary">
            Cancel
          </button>
        )
      }
    >
      {/* DROP STAGE */}
      {stage === 'drop' && (
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          className="flex h-56 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 text-center transition hover:border-blue-400 hover:bg-blue-50 dark:border-gray-700 dark:bg-gray-800/40"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="mb-2 h-8 w-8 text-gray-400" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Drop the resume PDF here, or click to choose
          </p>
          <p className="mt-1 text-xs text-gray-500">PDF or DOCX, up to 10 MB</p>
          <p className="mt-3 text-xs text-gray-500">
            Everything is auto-extracted. You only fix what looks wrong.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.doc,application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
            }}
          />
          {error && (
            <p className="mt-3 flex items-center gap-1 text-xs text-red-600">
              <AlertCircle className="h-3.5 w-3.5" />
              {error}
            </p>
          )}
        </div>
      )}

      {/* EXTRACTING */}
      {stage === 'extracting' && (
        <div className="flex h-56 flex-col items-center justify-center text-center">
          <Loader2 className="mb-2 h-8 w-8 animate-spin text-blue-500" />
          <p className="text-sm text-gray-700 dark:text-gray-200">Reading {fileName}…</p>
          <p className="mt-1 text-xs text-gray-500">
            Claude is extracting name, contact, work history, and preferences.
          </p>
        </div>
      )}

      {/* REVIEW */}
      {(stage === 'review' || stage === 'saving') && profile && (
        <div className="space-y-4">
          <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
            Auto-filled from <code>{fileName}</code>. Edit only what looks wrong.
            {reviewFlags.length > 0 && (
              <span className="ml-1">
                Items to double-check: {reviewFlags.join(', ')}.
              </span>
            )}
          </div>

          <label className="block text-xs font-medium text-gray-700">
            Applicant ID
            <input
              value={applicantId}
              onChange={(e) =>
                setApplicantId(
                  e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''),
                )
              }
              className="input mt-1"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            {PERSONAL_FIELDS.map(([key, label]) => (
              <label key={key} className="block text-xs font-medium text-gray-700">
                {label}
                <input
                  value={profile.personal?.[key] ?? ''}
                  onChange={(e) => setPersonalField(key, e.target.value)}
                  className="input mt-1"
                />
              </label>
            ))}
          </div>

          <details className="rounded-md bg-gray-50 px-3 py-2 text-xs dark:bg-gray-800/40">
            <summary className="cursor-pointer font-medium text-gray-700 dark:text-gray-200">
              Work history ({profile.work_history?.length ?? 0}) and preferences (advanced)
            </summary>
            <pre className="mt-2 overflow-auto text-xs text-gray-600 dark:text-gray-300">
              {JSON.stringify(
                {
                  work_history: profile.work_history,
                  education: profile.education_history,
                  preferences: profile.preferences,
                  eeo: profile.eeo,
                },
                null,
                2,
              )}
            </pre>
            <p className="mt-2 text-xs text-gray-500">
              These are editable in <em>Resumes → Profile editor</em> after creation.
            </p>
          </details>

          {error && (
            <p className="flex items-center gap-1 text-xs text-red-600">
              <AlertCircle className="h-3.5 w-3.5" />
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
